/**
 * The investigation CTA (label: ASK_AHOY_LABEL below) on a failed
 * automation run.
 *
 * On click, opens a brand-new conversation seeded with an investigation
 * prompt — never auto-sent, only prefilled into the composer. Where the
 * conversation lands depends on whether the current user can edit the
 * automation in the builder:
 *
 * - can edit → a new builder conversation grounded on the automation, opened
 *   directly in edit mode (an "editors" seat);
 * - otherwise → an ordinary "chat about this automation" conversation, with
 *   no write access to the automation itself (a "chat" seat).
 *
 * The prompt is built from fixed copy, the run's timestamp/provenance, and
 * the reason sentence already rendered on the timeline event — it never
 * includes the automation script's raw log (`payload.summary`), since that
 * text is script-controlled and must not be interpolated into an agent
 * prompt. See `buildAutomationFailurePrompt` in `automationFailureChat.ts`.
 */

import { MessageChatCircle } from '@untitledui/icons';
import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

import { useDisplayTimezone } from '@/hooks/useDisplayTimezone';
import { useFeatureAccess } from '@/hooks/usePermissions';
import { Button } from '@/lib/base/buttons/button';
import type { EntityTypeName } from '@/lib/crm/core/types';
import { useCrmObjectLive } from '@/lib/crm/hooks';
import { useCanEditInBuilder } from '@/lib/crm/hooks/useCanEditInBuilder';
import { getDisplayName } from '@/lib/crm/utils/getDisplayName';
import { track } from '@/lib/mixpanel';

import {
  buildAutomationFailureChatNavState,
  buildAutomationFailurePrompt,
} from './automationFailureChat';

// TODO(copy): confirm label with Laura
const ASK_AHOY_LABEL = 'Ask Ahoy what went wrong';

// Failure categories are internal slugs (apps/automations); only forward one
// that looks like a slug into analytics rather than leaking arbitrary text.
const CATEGORY_TRACKING_PATTERN = /^[a-z0-9_]{1,64}$/;

export interface AutomationFailureChatButtonProps {
  orgId: number;
  automationEntityId: string;
  automationEntityType: string;
  provenance: 'clock' | 'manual' | 'event' | undefined;
  occurredAt: string;
  reason: string | null;
  category: string | undefined;
  /** Id of the run's header element, for `aria-describedby`. */
  describedById: string;
}

export function AutomationFailureChatButton({
  orgId,
  automationEntityId,
  automationEntityType,
  provenance,
  occurredAt,
  reason,
  category,
  describedById,
}: AutomationFailureChatButtonProps) {
  const { hasFeature } = useFeatureAccess();
  const hasChatSeat = hasFeature('chat');
  const canEditInBuilder = useCanEditInBuilder(
    automationEntityType as EntityTypeName
  );
  const timezone = useDisplayTimezone();
  // No chat seat → the button never renders, so skip the subscription/fetch
  // entirely rather than looking up every failed automation for these users.
  const automationObject = useCrmObjectLive(
    hasChatSeat ? automationEntityId : undefined,
    orgId
  );
  const navigate = useNavigate();

  const handleClick = useCallback(() => {
    const prompt = buildAutomationFailurePrompt({
      provenance,
      occurredAt,
      timezone,
      reason,
      category,
    });
    const automationDisplayName = automationObject
      ? getDisplayName(automationObject, 'this automation')
      : 'this automation';
    const { to, state } = buildAutomationFailureChatNavState({
      orgId,
      automationEntityId,
      automationEntityType,
      automationDisplayName,
      canEditInBuilder,
      prompt,
    });

    track('automation_failure_chat_clicked', {
      destination: canEditInBuilder ? 'builder' : 'chat',
      provenance: provenance ?? 'unknown',
      category:
        category && CATEGORY_TRACKING_PATTERN.test(category)
          ? category
          : 'unknown',
    });

    navigate(to, { state });
  }, [
    automationEntityId,
    automationEntityType,
    automationObject,
    canEditInBuilder,
    category,
    navigate,
    occurredAt,
    orgId,
    provenance,
    reason,
    timezone,
  ]);

  if (!hasChatSeat) {
    return null;
  }

  return (
    // `secondary`, not `tertiary`: the CTA sits directly beneath the run's log
    // lines, and a ghost button there reads as another line of output rather
    // than a control. The outlined treatment separates it from the log while
    // staying below a brand-filled `primary` in emphasis — this is an offer of
    // help on a failed run, not the run's main action.
    <Button
      size='xs'
      color='secondary'
      iconLeading={MessageChatCircle}
      onClick={handleClick}
      aria-describedby={describedById}
    >
      {ASK_AHOY_LABEL}
    </Button>
  );
}
